import zod from 'zod';
import React, { useContext, useEffect, useMemo } from 'react';
import { AppContext } from '../../../context/AppContextProvider';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  DefenseUserTypes,
  getNameOfDefenseUserType,
} from '../../../services/IntegrationService/DefenseService/types';
import {
  useCreateDefenseIntegration,
  useGetDefenseIntegrationById,
  useUpdateDefenseIntegration,
} from '../../../services/IntegrationService/DefenseService/hooks/useMutations';
import { DefenseIntegrationFormSchema } from '../../../services/IntegrationService/DefenseService/schemas';
import { Grid2 } from '@mui/material';
import TextFieldComponent from '../../../components/TextFieldComponent';
import ButtonComponent from '../../../components/ButtonComponent';
import SwitchComponent from '../../../components/SwitchComponent';
import DropSelectComponent, {
  IDropSelectOptions,
} from '../../../components/DropSelectComponent';
import { t } from 'i18next';

interface DefenseEditionFormProps {
  defenseIdToUpdate?: number;
  handleSaveForm?: () => void;
}

type DefenseEditionFormInput = zod.infer<typeof DefenseIntegrationFormSchema>;

const DefenseEditionForm = ({
  defenseIdToUpdate,
  handleSaveForm,
}: DefenseEditionFormProps) => {
  const { showSnackbar } = useContext(AppContext);
  const { control, handleSubmit, reset } = useForm<DefenseEditionFormInput>({
    resolver: zodResolver(DefenseIntegrationFormSchema),
    defaultValues: {
      isActive: true,
      description: '',
      name: '',
      username: '',
      password: '',
      serverIp: '',
      httpPort: 80,
      mqttPort: 1883,
      userType: DefenseUserTypes.DOMAIN_USER,
    },
  });
  const { mutateAsync: mutateCreate } = useCreateDefenseIntegration();
  const { mutateAsync: mutateUpdate } = useUpdateDefenseIntegration();
  const { mutateAsync: mutateGet } = useGetDefenseIntegrationById();

  const userTypes: IDropSelectOptions[] = useMemo(() => {
    return Object.values(DefenseUserTypes).map((i) => {
      return {
        option: getNameOfDefenseUserType(i),
        value: i,
      };
    });
  }, []);

  const handleSave = async (data: DefenseEditionFormInput) => {
    if (defenseIdToUpdate) {
      await mutateUpdate(
        { id: defenseIdToUpdate, data },
        {
          onSuccess: () => {
            saveIntegration('Integração defense atualizada com sucesso.');
          },
        },
      );
      return;
    }
    await mutateCreate(data, {
      onSuccess: () => {
        saveIntegration('Integração defense criada com sucesso.');
      },
    });
  };

  const saveIntegration = (message: string) => {
    reset();
    showSnackbar(message, 'success');
    if (handleSaveForm) handleSaveForm();
  };

  useEffect(() => {
    if (defenseIdToUpdate) {
      mutateGet(defenseIdToUpdate).then((i) =>
        reset(i, { keepDefaultValues: true }),
      );
    }
  }, [defenseIdToUpdate]);

  return (
    <Grid2 container size={12} spacing={2}>
      <Grid2 size={{ xs: 12, md: 6 }}>
        <Controller
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <TextFieldComponent
              value={value}
              errorMessage={error?.message}
              onChangeText={onChange}
              label={t('integrationsPage.defenseForms.name')}
            />
          )}
          name={'name'}
          control={control}
        />
      </Grid2>
      <Grid2 size={{ xs: 12, md: 6 }}>
        <Controller
          render={({ field: { value, onChange } }) => (
            <DropSelectComponent
              label={t('integrationsPage.defenseForms.userType')}
              valueSelected={value}
              options={userTypes}
              onChange={onChange}
              mb={0}
            />
          )}
          name={'userType'}
          control={control}
        />
      </Grid2>
      <Grid2 size={{ xs: 12, md: 6 }}>
        <Controller
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <TextFieldComponent
              value={value}
              errorMessage={error?.message}
              onChangeText={onChange}
              label={t('integrationsPage.defenseForms.username')}
            />
          )}
          name={'username'}
          control={control}
        />
      </Grid2>
      <Grid2 size={{ xs: 12, md: 6 }}>
        <Controller
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <TextFieldComponent
              value={value}
              errorMessage={error?.message}
              onChangeText={onChange}
              label={t('integrationsPage.defenseForms.password')}
              type="password"
            />
          )}
          name={'password'}
          control={control}
        />
      </Grid2>
      <Grid2 size={{ xs: 12, md: 6 }}>
        <Controller
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <TextFieldComponent
              value={value}
              errorMessage={error?.message}
              onChangeText={onChange}
              label={t('integrationsPage.defenseForms.ipServer')}
              mask={'000.000.000.000'}
              maxLength={15}
            />
          )}
          name={'serverIp'}
          control={control}
        />
      </Grid2>
      <Grid2 size={{ xs: 12, md: 6 }}>
        <Controller
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <TextFieldComponent
              value={value}
              errorMessage={error?.message}
              onChangeText={(val) => onChange(Number(val))}
              label={t('integrationsPage.defenseForms.httpDoorServer')}
              type="number"
            />
          )}
          name={'httpPort'}
          control={control}
        />
      </Grid2>
      <Grid2 size={{ xs: 12, md: 6 }}>
        <Controller
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <TextFieldComponent
              value={value}
              errorMessage={error?.message}
              onChangeText={(val) => onChange(Number(val))}
              label={t('integrationsPage.defenseForms.mqttDoorServer')}
              type="number"
            />
          )}
          name={'mqttPort'}
          control={control}
        />
      </Grid2>
      <Grid2 size={{ xs: 12, md: 6 }}>
        <Controller
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <TextFieldComponent
              value={value}
              errorMessage={error?.message}
              onChangeText={onChange}
              label={t('integrationsPage.defenseForms.description')}
              multiline
              maxRows={4}
            />
          )}
          name={'description'}
          control={control}
        />
      </Grid2>
      <Grid2 size={{ xs: 12, md: 6 }}>
        <Controller
          render={({ field: { value, onChange } }) => (
            <SwitchComponent
              label={t('integrationsPage.defenseForms.status')}
              value={value}
              onChange={onChange}
              color={'primary'}
            />
          )}
          name={'isActive'}
          control={control}
        />
      </Grid2>
      <Grid2 size={12} sx={{ mt: 2 }}>
        <ButtonComponent
          fullWidth
          type={'submit'}
          onClick={handleSubmit(handleSave)}
        >
          {t('integrationsPage.defenseForms.save')}
        </ButtonComponent>
      </Grid2>
    </Grid2>
  );
};

export default DefenseEditionForm;
